import React, { useState } from 'react'
import { styled } from '@mui/material/styles'
import { Helmet } from '@dr.pogodin/react-helmet'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, CardContent, Grid, Card as MuiCard, Divider as MuiDivider, Typography, Alert } from '@mui/material'
import { spacing, Stack } from '@mui/system'
import { RegisteredTextField } from '../../components/fields/TextField'

import UserApi from '../../api/UserApi'

const Divider = styled(MuiDivider)(spacing)
const Card = styled(MuiCard)(spacing)

/**
 * Password validation rules:
 * - At least 10 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 */
const VALID_PASSWORD = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{10,}$/

function NewPasswordFields({ label, label2, name = 'newPassword', pattern }) {
    const { t } = useTranslation()
    const [pw1, setPw1] = useState('')
    const validationPattern = pattern ?? {
        value: VALID_PASSWORD,
        message: t('pages.auth.profile.changePassword.validation.passwordPattern'),
    }

    name = name || 'newPassword'
    const name2 = name + '2'
    const toBlank = (v) => v || ''
    return (
        <>
            <RegisteredTextField
                name={name}
                label={label}
                type='password'
                validation={{
                    required: t('pages.auth.profile.changePassword.validation.fieldRequired'),
                    pattern: validationPattern,
                }}
                onChangeValue={(v) => setPw1(v)}
            />
            <RegisteredTextField
                name={name2}
                label={label2}
                type='password'
                validation={{
                    required: t('pages.auth.profile.changePassword.validation.fieldRequired'),
                    validate: (v) =>
                        toBlank(v) === toBlank(pw1) ||
                        t('pages.auth.profile.changePassword.validation.passwordsMismatch'),
                }}
            />
        </>
    )
}

function UpdatePasswordForm() {
    const { t } = useTranslation()
    const formMethods = useForm({ mode: 'onSubmit', shouldFocusError: true })
    const { handleSubmit, reset } = formMethods
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState({ type: '', text: '' })
    const userApi = new UserApi()

    const onSubmit = async (data) => {
        const { password, newPassword } = data

        setLoading(true)
        setMessage({ type: '', text: '' })

        try {
            const response = await userApi.updateMe({ password: { currentPassword: password, newPassword } })
            if (response.errorCode) {
                if (response.errorCode === 'WrongPassword') {
                    setMessage({
                        type: 'error',
                        text: t('pages.auth.profile.changePassword.wrongCurrentPassword'),
                    })
                } else if (response.errorCode === 'InvalidPassword') {
                    setMessage({
                        type: 'error',
                        text: t('pages.auth.profile.changePassword.invalidNewPassword'),
                    })
                } else {
                    setMessage({
                        type: 'error',
                        text: t('pages.auth.profile.changePassword.updateFailed'),
                    })
                }
                setLoading(false)
                return
            }
            setMessage({ type: 'success', text: t('pages.auth.profile.changePassword.success') })
            reset()
        } catch (error) {
            setMessage({ type: 'error', text: t('pages.auth.profile.changePassword.updateFailed') })
        }

        setLoading(false)
    }

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Card mb={6}>
                    <CardContent>
                        <Stack spacing={3}>
                            <Typography variant='h6' gutterBottom>
                                {t('pages.auth.profile.changePassword.title')}
                            </Typography>

                            {message.text && (
                                <Alert severity={message.type === 'error' ? 'error' : 'success'}>{message.text}</Alert>
                            )}

                            <RegisteredTextField
                                name='password'
                                label={t('pages.auth.profile.changePassword.currentPassword')}
                                type='password'
                                validation={{
                                    required: t('pages.auth.profile.changePassword.validation.fieldRequired'),
                                }}
                            />

                            <NewPasswordFields
                                label={t('pages.auth.profile.changePassword.newPassword')}
                                label2={t('pages.auth.profile.changePassword.newPasswordAgain')}
                            />

                            <Button
                                variant='contained'
                                color='primary'
                                sx={{ width: 'auto', alignSelf: 'flex-start' }}
                                disabled={loading}
                                type='submit'
                            >
                                {loading
                                    ? t('pages.auth.profile.changePassword.submitting')
                                    : t('pages.auth.profile.changePassword.submit')}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </form>
        </FormProvider>
    )
}

function UserProfile() {
    const { t } = useTranslation()

    return (
        <React.Fragment>
            <Helmet title={t('pages.auth.profile.helmetTitle')} />
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'inline',
                }}
            >
                {t('pages.auth.profile.title')}
            </Typography>
            <Divider my={6} />
            <Grid container spacing={6}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <UpdatePasswordForm />
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default UserProfile
